import React, { useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";

import styled from "styled-components";

import CrosswordProvider, { crosswordProviderPropTypes } from "./CrosswordProvider";
import CrosswordGrid from "./CrosswordGrid";
import DirectionClues from "./DirectionClues";

const CluesWrapper = styled.div.attrs((/* props */) => ({
	className: "clues",
}))`
	padding-right: 1em;
	flex: 1 2 20%;

	@media (max-width: ${(props) => props.theme.columnBreakpoint}) {
		margin-top: 2em;
	}

	.direction {
		margin-bottom: 2em;

		.header {
			margin-top: 0;
			margin-bottom: 0.5em;
			text-shadow: 0px 4px 3px rgba(0, 0, 0, 0.4), 0px 8px 13px rgba(0, 0, 0, 0.1), 0px 18px 23px rgba(0, 0, 0, 0.1);
			color: #503203;
			font-family: Rubik;
			font-size: 28px;
			font-weight: bolder;
			text-align: center;
		}

		div {
			margin-top: 0.5em;
			border-radius: 0px 10px 10px 0px;
			padding: 10px;
			background-color: #503203;
			font-family: Rubik;
			font-size: 22px;
			font-weight: bold;
			letter-spacing: 0.8px;
			text-align: left;
			color: #fff;
			text-shadow: 1px 2px 4px #000;

			@media only screen and (min-width: 960px) and (max-width: 1280px) {
				font-size: 18px;
			}
			@media only screen and (min-width: 2000px) {
				font-size: 28px;
			}
		}
	}
`;

const crosswordPropTypes = {
	...crosswordProviderPropTypes,

	/** the label for the "across" clues */
	acrossLabel: PropTypes.string,

	/** the label for the "down" clues */
	downLabel: PropTypes.string,
};

// @ts-expect-error TS doesn't allow non-optional props to be deleted, but we're
// building this into a new type!
delete crosswordPropTypes.children;

// This somewhat non-obvious construction is to get the typings from
// CrosswordProvider where they are "better" than the default inferred types.

/**
 * The default export from the react-crossword library, `Crossword` renders an
 * answer grid and clues in a basic layout and provides access to most
 * functionality.
 */
const Crossword = React.forwardRef(({ acrossLabel, downLabel, ...props }, ref) => {
	const providerRef = useRef(null);

	// expose some imperative methods
	useImperativeHandle(
		ref,
		() => ({
			/**
			 * Sets focus to the crossword component.
			 */
			focus: () => providerRef.current?.focus(),

			/**
			 * Resets the entire crossword; clearing all answers in the grid and
			 * also any persisted data.
			 */
			reset: () => providerRef.current?.reset(),

			/**
			 * Fills all the answers in the grid and calls the `onLoadedCorrect`
			 * callback with _**every**_ answer.
			 */
			fillAllAnswers: () => providerRef.current?.fillAllAnswers(),

			/**
			 * Returns whether the crossword is entirely correct or not.
			 *
			 * @since 2.2.0
			 */
			isCrosswordCorrect: () => !!providerRef.current?.isCrosswordCorrect(),

			/**
			 * Sets the “guess” character for a specific grid position.
			 *
			 * @since 4.1.0
			 */
			setGuess: (row, col, guess) => providerRef.current?.setGuess(row, col, guess),
		}),
		[]
	);

	return (
		<CrosswordProvider {...props} ref={providerRef}>
			<CluesWrapper>
				<DirectionClues direction="across" label={acrossLabel} />
				<DirectionClues direction="down" label={downLabel} />
			</CluesWrapper>
			<CrosswordGrid />
		</CrosswordProvider>
	);
});

Crossword.displayName = "Crossword";
Crossword.propTypes = crosswordPropTypes;
Crossword.defaultProps = {
	...CrosswordProvider.defaultProps,
	acrossLabel: undefined,
	downLabel: undefined,
};

export default Crossword;
